import { supabase } from "@/lib/supabase";
import { withTimeout } from "@/lib/timeout";

export type LatLng = { lat: number; lng: number };
export type DirectionsWaypoint = LatLng & { id: string };

export interface DirectionsLeg {
  distance_m: number;
  duration_s: number;
}

export interface DriveDirectionsResult {
  /** Waypoint IDs in the drive order Google picked (when optimize=true). */
  orderedWaypointIds: string[];
  /** Decoded road geometry: pairs of [lat,lng] along actual roads. */
  polyline: Array<[number, number]>;
  /** Google's raw encoded-polyline string. Persist to DB so a saved route
   *  doesn't burn another Directions call on every page view. */
  polyline_encoded: string;
  /** Per-leg drive distance + duration. legs[0] is origin → first stop, etc. */
  legs: DirectionsLeg[];
  total_distance_m: number;
  total_duration_s: number;
}

export interface OptimizeDriveRouteInput {
  origin: LatLng;
  /** `null` for an open path (no return leg). Omit to default to round-trip via origin. */
  destination?: LatLng | null;
  waypoints: DirectionsWaypoint[];
  /** Let Google reorder waypoints for shortest drive. Default true. */
  optimize?: boolean;
}

/**
 * Invokes the `route-directions` edge function to get a real driving route.
 * Returns null if there are no waypoints (no API call worth making).
 */
export async function optimizeDriveRoute(
  input: OptimizeDriveRouteInput,
): Promise<DriveDirectionsResult | null> {
  if (!input.waypoints || input.waypoints.length === 0) return null;
  const body = {
    origin: input.origin,
    destination: input.destination ?? null,
    waypoints: input.waypoints,
    optimize: input.optimize !== false,
  };
  const { data, error } = await withTimeout(
    supabase.functions.invoke<DriveDirectionsResult>("route-directions", { body }),
    30_000,
    "Route directions",
  );
  if (error) throw error;
  if (!data) throw new Error("Empty response from route-directions");
  return data;
}
